/**
 * The window the watch party lives in.
 *
 * Three ways to hold it, and the choice sticks:
 *
 *   docked    in the column, where it started
 *   floating  a small window over the app that you drag where you like and
 *             resize from its lower right corner
 *   full      the whole screen, through the browser's own fullscreen
 *
 * Nothing here knows what is being watched. It is the frame and the three
 * shapes; WatchTogether puts the picture and the controls inside it.
 *
 * Where you left a floating window is remembered per machine, because the
 * answer to "not over the chat" is different on every monitor. It is also
 * pushed back inside the window on every open, so a screen you unplugged
 * cannot strand it somewhere you can no longer reach.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';

export type Hold = 'dock' | 'float';

type Box = { x: number; y: number; w: number; h: number };

const KEY = 'pogu.watchBox';
const MIN_W = 320;
const MIN_H = 220;

function clampToWindow(b: Box): Box {
  const w = Math.min(Math.max(b.w, MIN_W), Math.max(MIN_W, window.innerWidth - 24));
  const h = Math.min(Math.max(b.h, MIN_H), Math.max(MIN_H, window.innerHeight - 24));
  return {
    w,
    h,
    x: Math.min(Math.max(b.x, 8), Math.max(8, window.innerWidth - w - 8)),
    y: Math.min(Math.max(b.y, 8), Math.max(8, window.innerHeight - h - 8))
  };
}

function firstBox(): Box {
  const w = 460;
  const h = 300;
  return { w, h, x: Math.max(8, window.innerWidth - w - 24), y: Math.max(8, window.innerHeight - h - 24) };
}

function load(): Box {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return firstBox();
    const b = JSON.parse(raw) as Box;
    if (typeof b?.x !== 'number' || typeof b?.w !== 'number') return firstBox();
    return clampToWindow(b);
  } catch { return firstBox(); }
}

export function useWatchFrame() {
  const [hold, setHold] = useState<Hold>('dock');
  const [box, setBox] = useState<Box>(() => (typeof window === 'undefined' ? { x: 0, y: 0, w: 460, h: 300 } : firstBox()));
  const shell = useRef<HTMLElement | null>(null);
  const [full, setFull] = useState(false);

  /* Only read the remembered place when it is first needed. */
  useEffect(() => { if (hold === 'float') setBox(load()); }, [hold]);

  useEffect(() => {
    if (hold !== 'float') return;
    try { localStorage.setItem(KEY, JSON.stringify(box)); } catch { /* private window */ }
  }, [hold, box]);

  /* A window that got smaller must not leave it off the edge. */
  useEffect(() => {
    if (hold !== 'float') return;
    const onResize = () => setBox((b) => clampToWindow(b));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [hold]);

  /*
   * Fullscreen that cannot fail.
   *
   * requestFullscreen is the nice version - it covers the taskbar too - but
   * it is refused for a list of reasons the person pressing the button will
   * never care about: no user gesture the browser believes in, a permissions
   * policy on an embedded page, a frameless window that will not take it.
   * When it is refused the button appeared to do nothing at all.
   *
   * So filling the window is the app's own state and always happens, and the
   * real thing is asked for on top of that. Refused, the panel still covers
   * everything Pugo draws, which is what was wanted; granted, it covers the
   * rest of the screen as well.
   */
  useEffect(() => {
    const sync = () => { if (!document.fullscreenElement) setFull((was) => (wantedFull.current ? was : false)); };
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  /* True while the person wants it full, whatever the browser decided. */
  const wantedFull = useRef(false);

  const toggleFull = useCallback(() => {
    const el = shell.current;
    const next = !wantedFull.current;
    wantedFull.current = next;
    setFull(next);
    if (!next) {
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => { /* already out */ });
      return;
    }
    void el?.requestFullscreen?.().catch(() => { /* the in-app fill stands */ });
  }, []);

  /*
   * Lift the stage while this is out.
   *
   * main.stage carries isolation: isolate, so it is a stacking context of its
   * own and nothing inside it can paint above the title bar however high its
   * z-index goes - a fullscreen video came out with the search bar sitting on
   * top of it. Rather than move the panel into another part of the page and
   * reload the video doing it, the stage itself is lifted for as long as the
   * panel is filling or floating, and put back afterwards.
   */
  useEffect(() => {
    const lift = full || hold === 'float';
    const root = document.documentElement;
    if (lift) root.dataset.watchFill = '1';
    else delete root.dataset.watchFill;
    return () => { delete root.dataset.watchFill; };
  }, [full, hold]);

  /* Escape leaves it, the same as it leaves real fullscreen. */
  useEffect(() => {
    if (!full) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      wantedFull.current = false;
      setFull(false);
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [full]);

  /* The gesture reads the box without re-subscribing to every pixel of it. */
  const boxRef = useRef(box);
  boxRef.current = box;

  /*
   * Dragging and resizing, on pointer capture.
   *
   * Capture is what keeps the gesture alive when the pointer crosses the
   * video - an iframe would otherwise swallow every move the moment the
   * cursor entered it, and the window would stick to the edge of it.
   */
  const grab = useCallback((mode: 'move' | 'size') => (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    /* The bar you drag by is also the bar the buttons live on. Starting a
       drag from one of them would swallow its press, because preventDefault
       on a pointerdown is what stops the click that follows. */
    if ((e.target as HTMLElement).closest('button, a, input, select, textarea')) return;
    e.preventDefault();
    const start = { px: e.clientX, py: e.clientY };
    const from = { ...boxRef.current };
    const node = e.currentTarget as HTMLElement;
    node.setPointerCapture(e.pointerId);

    const move = (ev: PointerEvent) => {
      const dx = ev.clientX - start.px;
      const dy = ev.clientY - start.py;
      setBox(clampToWindow(
        mode === 'move'
          ? { ...from, x: from.x + dx, y: from.y + dy }
          : { ...from, w: from.w + dx, h: from.h + dy }
      ));
    };
    const done = () => {
      node.removeEventListener('pointermove', move);
      node.removeEventListener('pointerup', done);
      node.removeEventListener('pointercancel', done);
    };
    node.addEventListener('pointermove', move);
    node.addEventListener('pointerup', done);
    node.addEventListener('pointercancel', done);
  }, []);

  return { hold, setHold, box, shell, full, toggleFull, grab };
}

/** The resize corner. Its own element so the whole edge is not a handle. */
export function SizeGrip({ onGrab }: { onGrab: (e: React.PointerEvent) => void }) {
  return (
    <span
      role="presentation"
      onPointerDown={onGrab}
      title="Resize"
      className={cn(
        'absolute right-0 bottom-0 z-[3] h-5 w-5 cursor-nwse-resize touch-none',
        'after:absolute after:right-1 after:bottom-1 after:h-2.5 after:w-2.5',
        'after:border-r-2 after:border-b-2 after:border-white/45 after:content-[""]'
      )}
    />
  );
}
